import {useCallback, useEffect, useRef, useState} from "react";
import {loadUserOptions, userOptions} from "../../user";
import {getListData, getListDataWStateFilter} from "./dealsApi";

/**
 *
 * @returns {{fetchDeals: (function(): Promise<unknown>), loading: *}}
 */
export const useDeals = () => {
  const [loading, setLoading] = useState(true);
  const [dealsList, setDealsList] = useState([]);

  const currentOffsetRef = useRef(0);
  const currentDealsNum = useRef(0);
  const onFilter = useRef(null);

  useEffect(() => {
    loadUserOptions()
    fetchDeals();
  }, []);

  const fetchDeals = useCallback(async (offset) => {
    let startFrom = (offset || 0);

    if (startFrom < 0)
      startFrom = 0;

    currentOffsetRef.current = startFrom;

    try {
      const response = await getListData(startFrom);

      currentDealsNum.current = response.data?.length || 0;

      if (response.ok && response?.data.length) {
        setDealsList(prepareFetchedDealsData(response.data));
      } else {
        setDealsList([]);
      }
    } catch (error) {
      setDealsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDealsData = useCallback((offset) => {
    resetFilter()
    fetchDeals(offset);
  });

  const loadListDataWStateFilter = useCallback(async (state, offset) => {
    let startFrom = (offset || 0);

    if (startFrom < 0)
      startFrom = 0;

    currentOffsetRef.current = startFrom;
    onFilter.current = state;

    const result = await getListDataWStateFilter(state, startFrom);

    if (result.ok) {
      setDealsList(prepareFetchedDealsData(result.data));
      currentDealsNum.current = result.data?.length || 0;
    } else {}
  }, []);

  const loadNextDeals = async () => {
    const offsetDeafult = userOptions.dealsListLimit

    if (onFilter.current)
      loadListDataWStateFilter(onFilter.current, (currentOffsetRef.current + offsetDeafult))
    else
      loadDealsData((currentOffsetRef.current + offsetDeafult))
  }

  const loadPrevDeals = async () => {
    const offsetDeafult = userOptions.dealsListLimit
    if (onFilter.current)
      loadListDataWStateFilter(onFilter.current, (currentOffsetRef.current - offsetDeafult))
    else
      loadDealsData((currentOffsetRef.current - offsetDeafult))
  }

  const pinDeal = useCallback((dealId) => {
    setDealsList((prevDeals) => {
      const pinned = [];
      const unpinned = [];

      prevDeals.forEach((item) => {
        if (item.id === dealId) {
          pinned.push({...item, pinned: dealId});
        } else {
          unpinned.push(item);
        }
      });

      return [...pinned, ...unpinned];
    });
  })

  const unpinDeal = useCallback((dealId) => {
    setDealsList((prevDeals) => {
      const pinned = [];
      const unpinned = [];

      prevDeals.forEach((item) => {
        if (item.id === dealId) {
          unpinned.push({...item, pinned: null});
        } else if (item.pinned) {
          pinned.push(item);
        } else {
          unpinned.push({...item})
        }
      });

      return [...pinned, ...unpinned];
    });
  }, [])

  const setDisablePagination = async () => {
    currentDealsNum.current = 0;
  }

  const resetFilter = () => {
    onFilter.current = null;
  }

  const resetOffset = () => {
    currentOffsetRef.current = 0;
  }

  return {
    loadDealsData,
    loading,
    dealsList,
    dealsNum: currentDealsNum.current,
    currentListOffset: currentOffsetRef.current,
    setDealsList,
    loadNextDeals,
    loadPrevDeals,
    setDisablePagination,
    loadListDataWStateFilter,
    pinDeal,
    unpinDeal,
    resetOffset
  };
};

export const prepareFetchedDealsData = (rawData) => {
  if (Array.isArray(rawData) && rawData.length) {
    const pinned = rawData.filter((item) => item.pinned);
    const nonPinned = rawData.filter((item) => !item.pinned);

    return [...pinned, ...nonPinned]
  }

  return [];
}